export default class BrowserInfo {
  os_name: string;
  browser_name: string;
  browser_version: string;
  is_mobile: boolean;

  constructor() {
    this.browser_name = 'unknown';
    this.browser_version = '';
    this.os_name = 'Unknown OS';
    this.is_mobile = false;

    if (typeof navigator === 'undefined') return;

    const uaData = (navigator as any).userAgentData;

    if (uaData) {
      const platform = uaData.platform || 'Unknown OS';
      const brands: Array<{ brand: string; version: string }> = uaData.brands || [];

      let browserName = 'Unknown Browser';
      let browserVersion = '';

      const realBrand =
        brands.find((b) => !/Not.*Brand/i.test(b.brand)) ||
        brands[0];

      if (realBrand) {
        browserName = realBrand.brand;
        browserVersion = realBrand.version;
      }

      this.browser_name = this.normalizeBrowser(browserName);
      this.browser_version = browserVersion;
      this.os_name = this.normalizeOs(platform);
      this.is_mobile = !!uaData.mobile;
    } else {
      const ua = navigator.userAgent;
      this.os_name = this.detectOsFromUA(ua);
      this.browser_name = this.detectBrowserFromUA(ua);
      this.browser_version = this.detectBrowserVersionFromUA(ua);
      this.is_mobile = /Android|iPhone|iPad|iPod/i.test(ua);
    }
  }

  normalizeOs(platform: string): string {
    const p = platform.toLowerCase();
    if (p.includes('android')) return 'Android';
    if (p.includes('ios')) return 'iOS';
    if (p.includes('mac')) return 'macOS';
    if (p.includes('win')) return 'Windows';
    if (p.includes('linux')) return 'Linux';
    return platform;
  }

  normalizeBrowser(name: string): string {
    const n = name.toLowerCase();
    if (n.includes('chrome')) return 'Chrome';
    if (n.includes('chromium')) return 'Chromium';
    if (n.includes('edge')) return 'Edge';
    if (n.includes('opera')) return 'Opera';
    if (n.includes('safari')) return 'Safari';
    if (n.includes('firefox')) return 'Firefox';
    return name;
  }

  detectOsFromUA(ua: string): string {
    const s = ua.toLowerCase();
    if (s.includes('android')) return 'Android';
    if (s.includes('iphone') || s.includes('ipad') || s.includes('ipod')) return 'iOS';
    if (s.includes('windows')) return 'Windows';
    if (s.includes('mac os x') || s.includes('macintosh')) return 'macOS';
    if (s.includes('linux')) return 'Linux';
    return 'Unknown OS';
  }

  detectBrowserFromUA(ua: string): string {
    if (/Edg\//.test(ua)) return 'Edge';
    if (/OPR\//.test(ua) || /Opera\//.test(ua)) return 'Opera';
    if (/Chrome\//.test(ua) && !/Edg\//.test(ua) && !/OPR\//.test(ua)) return 'Chrome';
    if (/Firefox\//.test(ua)) return 'Firefox';
    if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'Safari';
    return 'Unknown Browser';
  }

  detectBrowserVersionFromUA(ua: string): string {
    const match =
      ua.match(/Edg\/([\d.]+)/) ||
      ua.match(/OPR\/([\d.]+)/) ||
      ua.match(/Chrome\/([\d.]+)/) ||
      ua.match(/Firefox\/([\d.]+)/) ||
      ua.match(/Version\/([\d.]+).*Safari/);

    return match?.[1] ?? '';
  }

  get_info(hasWebGPU: boolean) {
    return {
      hasWebGPU,
      osName: this.os_name,
      browserName: this.browser_name,
      browserVersion: this.browser_version,
      isMobile: this.is_mobile,
    };
  }

  get_advice(has_webgpu: boolean) {
    const isAndroid = this.os_name === 'Android';
    const isIOS = this.os_name === 'iOS';
    const isWindows = this.os_name === 'Windows';
    const isMac = this.os_name === 'macOS';
    const isLinux = this.os_name === 'Linux';

    const isChrome = this.browser_name === 'Chrome';
    const isEdge = this.browser_name === 'Edge';
    const isSafari = this.browser_name === 'Safari';
    const isFirefox = this.browser_name === 'Firefox';
    const isOpera = this.browser_name === 'Opera';

    if (has_webgpu) {
      return {
        title: 'WebGPU is available',
        color: 'teal',
        summary: 'This browser already exposes WebGPU, so you should be able to use the GPU features in this app.',
        steps: [
          'If something still fails, the issue is likely app-specific rather than browser support.',
        ],
        primaryLabel: '',
        primaryHref: '',
      };
    }

    if (isAndroid && !isChrome) {
      return {
        title: 'This Android browser does not expose WebGPU',
        color: 'yellow',
        summary:
          'Your phone may support WebGPU at the operating-system level, but this browser does not currently expose it. Chrome on Android is often the best browser to try next.',
        steps: [
          'Install or open Chrome on this Android device.',
          'Open this app again in Chrome.',
          'If it still does not work, update Chrome from Google Play and try again.',
        ],
        primaryLabel: 'Install Chrome',
        primaryHref: 'https://play.google.com/store/apps/details?id=com.android.chrome',
      };
    }

    if (isAndroid && isChrome) {
      return {
        title: 'Chrome is installed, but WebGPU is still unavailable',
        color: 'yellow',
        summary:
          'This Android device or Chrome build may not currently expose WebGPU. Updating Chrome and Android may help.',
        steps: [
          'Update Chrome from Google Play.',
          'Update Android system software if an update is available.',
          'Restart Chrome and try again.',
        ],
        primaryLabel: 'Open Chrome in Play Store',
        primaryHref: 'https://play.google.com/store/apps/details?id=com.android.chrome',
      };
    }

    if (isIOS) {
      return {
        title: 'WebGPU support is limited on this Apple mobile browser',
        color: 'yellow',
        summary:
          'On iPhone and iPad, browser engine support and rollout can limit WebGPU availability. Trying the newest OS and browser version is your best next step.',
        steps: [
          'Update iOS or iPadOS to the newest available version.',
          'Try again in the latest Safari.',
          'If your device is older, WebGPU may still be unavailable.',
        ],
        primaryLabel: '',
        primaryHref: '',
      };
    }

    if ((isWindows || isMac || isLinux) && isFirefox) {
      return {
        title: 'Firefox does not currently expose WebGPU here',
        color: 'yellow',
        summary:
          'Your operating system may support GPU features, but this browser build does not expose WebGPU for this app.',
        steps: [
          'Try opening this app in Chrome or Edge.',
          'Make sure your browser is fully updated.',
          'If needed, update your graphics drivers or operating system.',
        ],
        primaryLabel: isWindows ? 'Download Chrome' : 'Get Chrome',
        primaryHref: 'https://www.google.com/chrome/',
      };
    }

    if ((isWindows || isMac || isLinux) && (isOpera || (!isChrome && !isEdge && !isSafari))) {
      return {
        title: 'This browser may not support WebGPU',
        color: 'yellow',
        summary:
          'Your computer may support WebGPU, but this browser does not appear to expose it for this app.',
        steps: [
          'Try Chrome or Edge.',
          'Update your browser to the latest version.',
          'If it still fails, check GPU driver and OS updates.',
        ],
        primaryLabel: isWindows ? 'Download Chrome' : 'Get Chrome',
        primaryHref: 'https://www.google.com/chrome/',
      };
    }

    if ((isWindows || isMac || isLinux) && (isChrome || isEdge || isSafari)) {
      return {
        title: 'This browser should support WebGPU, but it is unavailable right now',
        color: 'yellow',
        summary:
          'WebGPU may be blocked by browser version, GPU driver issues, disabled hardware acceleration, or platform restrictions.',
        steps: [
          'Update your browser to the newest version.',
          'Make sure hardware acceleration is enabled.',
          'Update your graphics drivers and operating system.',
          'Restart the browser and try again.',
        ],
        primaryLabel: '',
        primaryHref: '',
      };
    }

    return {
      title: 'WebGPU is not available in this browser',
      color: 'yellow',
      summary:
        'This app needs WebGPU, but the current browser does not expose it on this device.',
      steps: [
        'Try a newer browser.',
        'Update your operating system and browser.',
        'If possible, try Chrome or Edge.',
      ],
      primaryLabel: 'Get Chrome',
      primaryHref: 'https://www.google.com/chrome/',
    };
  }
}
